import { otherTypeOrder, typeOrder } from "constants/AppConstants";
import { FormValues } from "pages/Tenant";
import { DynamicItem } from "store/tenant/types";

type FlatData = { [key: string]: any };


export function splitDot(str: string): string[] {
  return str.split('.');
}



export const getNestedError = (errors: any, path: string): any => {
  return path.split('.').reduce((acc, key) => {
    // If key is a number string, convert to number for array access
    const k = /^\d+$/.test(key) ? Number(key) : key;
    return acc && acc[k] ? acc[k] : undefined;
  }, errors);
}



/**
 * Converts a date-input value (YYYY-MM-DD) to the dd/mm/yyyy format the API expects.
 * Returns "" for empty/invalid input so the backend validation reports it.
 */
export function toApiDate(value?: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
}

export function convertToStageOneRequestedFormat(data: FlatData, agentId: string, formId: string, addressPlaceId: string): {
  tenants: Array<{ type: string; first_name: string; last_name: string; email: string; mobile_number: string }>;
  property_address: string;
  agent_id: string;
  form_id: string;
  place_id: string;
  tenancy_agreement_date?: string;
} {
  const tenants: Array<{ type: string; first_name: string; last_name: string; email: string; mobile_number: string }> = [];
  let tenantIndex = 1;


  // Find all tenant indices present in the data
  while (data[`tenant${tenantIndex}_first_name`] || data[`tenant${tenantIndex}_last_name`] || data[`tenant${tenantIndex}_email`] || data[`tenant${tenantIndex}_phone`]) {
    tenants.push({
      type: tenantIndex === 1 ? "primary" : "co-tenant",
      first_name: data[`tenant${tenantIndex}_first_name`] || "",
      last_name: data[`tenant${tenantIndex}_last_name`] || "",
      email: data[`tenant${tenantIndex}_email`] || "",
      mobile_number: data[`tenant${tenantIndex}_phone`] || data["phone"] || ""
    });
    tenantIndex++;
  }

  // If no indexed tenants, fallback to single tenant fields
  if (tenants.length === 0 && (data["tenant1_first_name"] || data["tenant1_last_name"] || data["tenant1_email"] || data["phone"])) {
    tenants.push({
      type: "primary",
      first_name: data["tenant1_first_name"] || "",
      last_name: data["tenant1_last_name"] || "",
      email: data["tenant1_email"] || "",
      mobile_number: data["phone"] || ""
    });
  }

  return {
    tenants,
    property_address: data["property_address"] || "",
    place_id: addressPlaceId || "",
    agent_id: agentId || "",
    form_id: formId || "",
    // NSW only: present when the template renders the tenancy agreement date field
    ...(data["tenancy_agreement_date"] ? { tenancy_agreement_date: toApiDate(data["tenancy_agreement_date"]) } : {})
  };
}

export const convertToStageTwoSubmittedFormat = (data: FormValues | null, primaryTenantsData: any, uploadedDocuments?: any, tenantId?: string, applicationId?: string, activeStage?: number, petsItems?: DynamicItem[] ) => {

  const stageOneData = primaryTenantsData?.data || {};
  const primaryTenants = stageOneData?.property?.tenants?.length
    ? stageOneData.property.tenants.slice(0, 1).map((tenant: any) => ({
        ...tenant,
        signingOrder: 1
      }))
    : [];
  const tenants = [
    {
      type: "primary",
      tenants: primaryTenants
    },
    {
      type: "co-tenant",
      tenants: data?.tenants ? Object.keys(data.tenants).sort((a, b) => parseInt(a) - parseInt(b)).map(id => ({
        first_name: data.tenants[id].firstName || "",
        last_name: data.tenants[id].lastName || "",
        email: data.tenants[id].email || "",
        mobile_number: data.tenants[id].mobile || "",
        signingOrder: Number(id)+1
      })) : []
    }
  ];

  
  // Pets
  const pets: any[] = [];
  // QLD only: assign a global 1-based sort order across all pets (in the
  // type-grouped display order this loop already follows) so the API / FLK can
  // sequence the per-pet PDF forms. Recomputed on every submit, so deleting a
  // pet closes the gap and reloaded saved pets re-sequence deterministically.
  const isQldSubmission = localStorage.getItem('pet_app_region') === 'qld';
  let sortOrder = 0;
  for (const petType of typeOrder) {
    if (data?.[petType]) {
      const petData = data?.[petType];
      const numberOfPets = petsItems
        ? petsItems.filter((item) => item.type === petType).length
        : 0;
      const petList = [];
      
      for (let i = 0; i < numberOfPets; i++) {
        // i is the 0-based index in the form data array
        // animal_index is 1-based position within the pet type (1, 2, 3...)
        const animalPosition = i + 1; // Convert to 1-based
        
        // Find the pet item by type and animal_index (position within type)
        const petId = petsItems
          ?.filter((item) => item.type === petType)
          .find((petItem) => petItem.animal_index === animalPosition)?.pet_id ?? "";
        
        const petObj: any = {
          pet_name: petData?.pet_name?.[i] ?? "",
          microchipNumber: petData?.microchipNumber?.[i] ?? "",
          // QLD region fields - use same field names as API
          registration_number: petData?.registration_number?.[i] ?? "",
          fur_colour_length: petData?.fur_colour_length?.[i] === "N/A" ? "" : (petData?.fur_colour_length?.[i] ?? ""),
          weight_height_length: petData?.weight_height_length?.[i] === "N/A" ? "" : (petData?.weight_height_length?.[i] ?? ""),
          pet_sex: petData?.pet_sex?.[i] === "N/A" ? "" : (petData?.pet_sex?.[i] ?? ""),
          choices: petData?.choices?.[i] ? [petData.choices[i]] : [],
          description: petData?.description?.[i] ?? "",
          description2: petData?.dont_have_reason?.[i] === true ? "" : petData?.description2?.[i] ?? "",
          dont_have_reason: petData?.dont_have_reason?.[i] ?? false,
          animal_count: petData?.animal_count?.[i] ?? 1,
          is_more_than_one: petData?.is_more_than_one?.[i] ?? false,
          tenant_understanding: petData?.tenant_understanding?.[i] ?? false,
          id: petId,
          uploads: filterDocumentsByPet(uploadedDocuments || [], petType, animalPosition) // Use same position
        };
        // Add type_of_animal for 'other' types, or for QLD region (all pet types)
        if ((otherTypeOrder?.includes(petType) || localStorage.getItem('pet_app_region') === 'qld') && petData?.type_of_animal?.[i]) {
          petObj.type_of_animal = petData.type_of_animal?.[i];
        }
        // For 'dog', add reason_for_exemption if present
        if ((petType === "dog" || petType === "cat") && petData?.reason_for_exemption?.[i]) {
          petObj.reason_for_exemption = petData?.reason_for_exemption?.[i];
        }
        // QLD: global 1-based sort order across all pet types (see note above).
        if (isQldSubmission) {
          petObj.sort_order = ++sortOrder;
        }
        petList.push(petObj);
      }
      pets.push({
        type: petType,
        pets: petList
      });
    }
   
  }

  return {
    rental_property_address: stageOneData?.property?.address || "",
    email_notification: data?.email_notification ?? false,
    sms_notification: data?.sms_notification ?? false,
    post_notification: data?.post_notification ?? false,
    in_person_notification: data?.in_person_notification ?? false,
    unique_link_token: applicationId || "",
    tenant_link: tenantId  || "",
    step: activeStage && activeStage === 3 && "final",
    tenants,
    pets
  };
}


/**
 * Filters documents by pet type and index.
 * 
 * @param documents - Array of uploaded documents with metadata
 * @param pet_type - The type of pet (dog, cat, etc.)
 * @param pet_index - The 1-based index of the pet within its type
 * @returns Filtered array of documents with only document_id and document_description
 * 
 * @remarks
 * - pet_index is 1-based (1, 2, 3...) matching the UI display
 * - Documents are filtered by both pet_type AND pet_index
 * - Returns only the fields needed for submission (document_id, document_description)
 */
export function filterDocumentsByPet(
    documents: Array<{
        document_id: string;
        document_description: string;
        pet_type: string;
        pet_index: number | null;
    }>,
    pet_type: string,
    pet_index: number
): Array<{ document_id: string; document_description: string }> {
    return documents
        .filter(
            doc =>
                doc.pet_type === pet_type &&
                doc.pet_index === pet_index
        )
        .map(doc => ({
            document_id: doc.document_id,
            document_description: doc.document_description
        }));
}

/**
 * Transforms flat animal form data into structured output.
 * @param input - The flat input object from the form.
 * @returns Structured array grouped by animal type.
 */
export const transformAnimalFormData = (
  inputData: Record<string, any>,
  uploadedDocumentsListArray: any,
  petPosition?: number,
  tenantItems?: DynamicItem[]
) => {
    const animalMap: Record<string, any[]> = {};

    Object.entries(inputData).forEach(([key, value]) => {
        const match = key.match(/^([a-zA-Z]+)\.([a-zA-Z0-9_]+)\.(\d+)$/);
        if (!match) return;
        const [, type, field, idx] = match;
        if (!animalMap[type]) animalMap[type] = [];
        if (!animalMap[type][+idx]) animalMap[type][+idx] = {};
        if (field === "choices") {
            animalMap[type][+idx][field] = [value];
        } else {
            animalMap[type][+idx][field] = value;
        }
    });

    return Object.entries(animalMap).map(([type, pets]) => ({
      type,
      pets: Array.isArray(pets)
        ? pets
          .filter(pet => pet != null)
          .map((pet: any) => {
            // QLD region fields use same names as API - no transformation needed
            return {
              ...pet,
              animal_count: pet.animal_count || 1,
              is_more_than_one: (pet.animal_count ?? 1) > 1,
              id: tenantItems?.find((item: DynamicItem) => item.uid === petPosition)?.pet_id ?? "",
              uploads: filterDocumentsByPet(uploadedDocumentsListArray ?? [], type, petPosition ?? (pets.indexOf(pet) + 1))
            };
          })
        : []
    }));
}